import { createAsyncThunk } from "@reduxjs/toolkit"
import { serverApi, handleApiResponse, handlePageResponse } from "@core/api/core"
import { CommonResponse, commonPageResult } from "@/types/common"
import {
  ChupyakCreatePayload,
  ChupyakPatchPayload,
  ChupyakPatientPrintData,
  ChupyakSerializer,
  ChupyakHiraSubmissionCreatePayload,
  ChupyakHiraSubmissionPatchPayload,
  ChupyakHiraSubmissionSerializer,
  ChupyakItemCreatePayload,
  ChupyakItemPatchPayload,
  ChupyakItemSerializer,
  ChupyakInboundCreatePayload,
  ChupyakInboundPatchPayload,
  ChupyakInboundSerializer,
  ChupyakStockCreatePayload,
  ChupyakStockPatchPayload,
  ChupyakStockSerializer,
  ChupyakOutboundCreatePayload,
  ChupyakOutboundPatchPayload,
  ChupyakOutboundSerializer,
  ChupyakListParams,
  ChupyakPageListParams,
  ChupyakHiraSubmissionListParams,
  ChupyakItemListParams,
  ChupyakInboundListParams,
  ChupyakStockListParams,
  ChupyakOutboundListParams,
} from "@/types/chupyak"

export const createChupyak = async (payload: ChupyakCreatePayload): Promise<ChupyakSerializer> => {
  const response = await serverApi.post<CommonResponse>("/chupyak/prescription/", payload)
  return handleApiResponse<ChupyakSerializer>(response)
}

export const patchChupyak = async (chupyakUuid: string, payload: ChupyakPatchPayload): Promise<ChupyakSerializer> => {
  const response = await serverApi.patch<CommonResponse>(`/chupyak/prescription/${chupyakUuid}/`, payload)
  return handleApiResponse<ChupyakSerializer>(response)
}

export const deleteChupyak = async (chupyakUuid: string): Promise<void> => {
  const response = await serverApi.delete<CommonResponse>(`/chupyak/prescription/${chupyakUuid}/`)
  handleApiResponse<void>(response)
}

export const getChupyakList = async (params?: ChupyakListParams): Promise<ChupyakSerializer[]> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/prescription/", {
    params: {
      encounter_item: params?.encounterItem,
      standard_prescription_code: params?.standardPrescriptionCode,
    },
  })
  return handleApiResponse<ChupyakSerializer[]>(response)
}

export const getChupyakPageList = async (params?: ChupyakPageListParams): Promise<commonPageResult<ChupyakSerializer>> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/prescription/", {
    params: {
      encounter_item: params?.encounterItem,
      standard_prescription_code: params?.standardPrescriptionCode,
      patient_uuid: params?.patientUuid,
      ordering: params?.ordering,
      from_date: params?.fromDate,
      to_date: params?.toDate,
      page: params?.page,
      page_size: params?.pageSize,
    },
  })
  return handlePageResponse<ChupyakSerializer>(response)
}

export const getNhisChupyakPendingCount = async (): Promise<number> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/prescription/pending-count/")
  return handleApiResponse<number>(response)
}

export const fetchNhisChupyakPendingCount = createAsyncThunk<number, void, { rejectValue: string }>(
  "nhisChupyakPending/fetchCount",
  async (_, { rejectWithValue }) => {
    try {
      const count = await getNhisChupyakPendingCount()
      return count
    } catch (error: any) {
      return rejectWithValue(error.message || "첩약건보 미제출 건수 조회 중 오류가 발생했습니다.")
    }
  }
)

export const getChupyak = async (chupyakUuid: string): Promise<ChupyakSerializer> => {
  const response = await serverApi.get<CommonResponse>(`/chupyak/prescription/${chupyakUuid}/`)
  return handleApiResponse<ChupyakSerializer>(response)
}

export const getChupyakPatientPrint = async (chupyakUuid: string): Promise<ChupyakPatientPrintData> => {
  const response = await serverApi.get<CommonResponse>(`/chupyak/prescription/${chupyakUuid}/patient-print/`)
  return handleApiResponse<ChupyakPatientPrintData>(response)
}

export const createChupyakHiraSubmission = async (payload: ChupyakHiraSubmissionCreatePayload): Promise<ChupyakHiraSubmissionSerializer> => {
  const response = await serverApi.post<CommonResponse>("/chupyak/hira-submission/", payload)
  return handleApiResponse<ChupyakHiraSubmissionSerializer>(response)
}

export const patchChupyakHiraSubmission = async (
  id: number,
  payload: ChupyakHiraSubmissionPatchPayload
): Promise<ChupyakHiraSubmissionSerializer> => {
  const response = await serverApi.patch<CommonResponse>(`/chupyak/hira-submission/${id}/`, payload)
  return handleApiResponse<ChupyakHiraSubmissionSerializer>(response)
}

export const deleteChupyakHiraSubmission = async (id: number): Promise<void> => {
  const response = await serverApi.delete<CommonResponse>(`/chupyak/hira-submission/${id}/`)
  handleApiResponse<void>(response)
}

export const getChupyakHiraSubmissionList = async (params?: ChupyakHiraSubmissionListParams): Promise<ChupyakHiraSubmissionSerializer[]> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/hira-submission/", {
    params: {
      chupyak: params?.chupyak,
    },
  })
  return handleApiResponse<ChupyakHiraSubmissionSerializer[]>(response)
}

export const getChupyakHiraSubmission = async (id: number): Promise<ChupyakHiraSubmissionSerializer> => {
  const response = await serverApi.get<CommonResponse>(`/chupyak/hira-submission/${id}/`)
  return handleApiResponse<ChupyakHiraSubmissionSerializer>(response)
}

export const createChupyakItem = async (payload: ChupyakItemCreatePayload): Promise<ChupyakItemSerializer> => {
  const response = await serverApi.post<CommonResponse>("/chupyak/item/", payload)
  return handleApiResponse<ChupyakItemSerializer>(response)
}

export const patchChupyakItem = async (id: number, payload: ChupyakItemPatchPayload): Promise<ChupyakItemSerializer> => {
  const response = await serverApi.patch<CommonResponse>(`/chupyak/item/${id}/`, payload)
  return handleApiResponse<ChupyakItemSerializer>(response)
}

export const deleteChupyakItem = async (id: number): Promise<void> => {
  const response = await serverApi.delete<CommonResponse>(`/chupyak/item/${id}/`)
  handleApiResponse<void>(response)
}

export const getChupyakItemList = async (params?: ChupyakItemListParams): Promise<ChupyakItemSerializer[]> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/item/", {
    params: {
      chupyak: params?.chupyak,
      product_code: params?.productCode,
      herbal_name: params?.herbalName,
      item_adjustment_type: params?.itemAdjustmentType,
    },
  })
  return handleApiResponse<ChupyakItemSerializer[]>(response)
}

export const getChupyakItem = async (id: number): Promise<ChupyakItemSerializer> => {
  const response = await serverApi.get<CommonResponse>(`/chupyak/item/${id}/`)
  return handleApiResponse<ChupyakItemSerializer>(response)
}

export const createChupyakInbound = async (payload: ChupyakInboundCreatePayload): Promise<ChupyakInboundSerializer> => {
  const response = await serverApi.post<CommonResponse>("/chupyak/inbound/", payload)
  return handleApiResponse<ChupyakInboundSerializer>(response)
}

export const patchChupyakInbound = async (id: number, payload: ChupyakInboundPatchPayload): Promise<ChupyakInboundSerializer> => {
  const response = await serverApi.patch<CommonResponse>(`/chupyak/inbound/${id}/`, payload)
  return handleApiResponse<ChupyakInboundSerializer>(response)
}

export const deleteChupyakInbound = async (id: number): Promise<void> => {
  const response = await serverApi.delete<CommonResponse>(`/chupyak/inbound/${id}/`)
  handleApiResponse<void>(response)
}

export const getChupyakInboundList = async (params?: ChupyakInboundListParams): Promise<ChupyakInboundSerializer[]> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/inbound/", {
    params: {
      product_code: params?.productCode,
      herbal_name: params?.herbalName,
      member: params?.member,
    },
  })
  return handleApiResponse<ChupyakInboundSerializer[]>(response)
}

export const getChupyakInbound = async (id: number): Promise<ChupyakInboundSerializer> => {
  const response = await serverApi.get<CommonResponse>(`/chupyak/inbound/${id}/`)
  return handleApiResponse<ChupyakInboundSerializer>(response)
}

export const createChupyakStock = async (payload: ChupyakStockCreatePayload): Promise<ChupyakStockSerializer> => {
  const response = await serverApi.post<CommonResponse>("/chupyak/stock/", payload)
  return handleApiResponse<ChupyakStockSerializer>(response)
}

export const patchChupyakStock = async (id: number, payload: ChupyakStockPatchPayload): Promise<ChupyakStockSerializer> => {
  const response = await serverApi.patch<CommonResponse>(`/chupyak/stock/${id}/`, payload)
  return handleApiResponse<ChupyakStockSerializer>(response)
}

export const deleteChupyakStock = async (id: number): Promise<void> => {
  const response = await serverApi.delete<CommonResponse>(`/chupyak/stock/${id}/`)
  handleApiResponse<void>(response)
}

export const getChupyakStockList = async (params?: ChupyakStockListParams): Promise<ChupyakStockSerializer[]> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/stock/", {
    params: {
      product_code: params?.productCode,
      herbal_name: params?.herbalName,
      is_active: params?.isActive,
    },
  })
  return handleApiResponse<ChupyakStockSerializer[]>(response)
}

export const getChupyakStock = async (id: number): Promise<ChupyakStockSerializer> => {
  const response = await serverApi.get<CommonResponse>(`/chupyak/stock/${id}/`)
  return handleApiResponse<ChupyakStockSerializer>(response)
}

export const createChupyakOutbound = async (payload: ChupyakOutboundCreatePayload): Promise<ChupyakOutboundSerializer> => {
  const response = await serverApi.post<CommonResponse>("/chupyak/outbound/", payload)
  return handleApiResponse<ChupyakOutboundSerializer>(response)
}

export const patchChupyakOutbound = async (id: number, payload: ChupyakOutboundPatchPayload): Promise<ChupyakOutboundSerializer> => {
  const response = await serverApi.patch<CommonResponse>(`/chupyak/outbound/${id}/`, payload)
  return handleApiResponse<ChupyakOutboundSerializer>(response)
}

export const deleteChupyakOutbound = async (id: number): Promise<void> => {
  const response = await serverApi.delete<CommonResponse>(`/chupyak/outbound/${id}/`)
  handleApiResponse<void>(response)
}

export const getChupyakOutboundList = async (params?: ChupyakOutboundListParams): Promise<ChupyakOutboundSerializer[]> => {
  const response = await serverApi.get<CommonResponse>("/chupyak/outbound/", {
    params: {
      chupyak_stock: params?.chupyakStock,
      chupyak_item: params?.chupyakItem,
    },
  })
  return handleApiResponse<ChupyakOutboundSerializer[]>(response)
}

export const getChupyakOutbound = async (id: number): Promise<ChupyakOutboundSerializer> => {
  const response = await serverApi.get<CommonResponse>(`/chupyak/outbound/${id}/`)
  return handleApiResponse<ChupyakOutboundSerializer>(response)
}
